<template>
  <a-modal
    :title="$t('components.billing_top_up.title')"
    :visible="visible"
    :confirmLoading="false"
    :width="440"
    :okText="$t('components.billing_top_up.top_up')"
    :cancelText="$t('components.billing_top_up.cancel')"
    :okButtonProps="{
      props: {
        loading: updating,
        disabled: formDisabled
      }
    }"
    :maskClosable="false"
    @ok="handleSubmit"
    @cancel="$emit('close')"
  >
    <a-spin :spinning="fetching">
      <a-form-model
        ref="formRef"
        :model="form"
        :rules="formRules"
        :colon="false"
      >
        <a-form-model-item
          :label="$t('components.billing_top_up.choice_method')"
          class="expanded-option__form-item"
          prop="paymentMethod"
          required
        >
          <a-radio-group
            class="billing-top-up__radio-group"
            v-model="form.paymentMethod"
          >
            <a-radio
              class="billing-top-up__radio-group__row"
              value="cashless"
            >
              {{ $t("components.billing_top_up.cashless_payment") }}
            </a-radio>

            <a-radio
              class="billing-top-up__radio-group__row"
              value="bonuses"
            >
              {{ $t("components.billing_top_up.owner_bonuses") }}
            </a-radio>
          </a-radio-group>
        </a-form-model-item>

        <div
          class="expanded-option"
          v-show="form.paymentMethod === 'bonuses'"
        >
          <a-alert
            v-if="!isOwner"
            :message="$t('components.billing_top_up.use_only_owner_account')"
            type="warning"
            showIcon
          />

          <!-- ONLY FOR OWNER ACCOUNT -->
          <template v-else>
            <a-form-model-item
              :label="$t('components.billing_top_up.from_owner_balance')"
              class="expanded-option__form-item"
            >
              <div class="billing-section">
                <div class="billing-section__icon">
                  <a-avatar
                    :size="36"
                    icon="user"
                    shape="square"
                    :src="userImageSrc"
                    alt=""
                  />
                </div>
                <div class="billing-section__description">
                  <span class="billing-section__strong">
                    <span v-if="user.first_name && user.last_name">
                      {{ user.first_name }} {{ user.last_name.charAt(0) }}.
                    </span>
                    <span v-else>{{ user.username }}</span>
                    +{{ mobilePhone }}
                  </span>
                  <div>
                    {{ getFormattedPrice(sellerBonuses) }}
                    <img
                      class="bonus-icon"
                      src="@/assets/icons/coin.svg"
                    />
                  </div>
                </div>
              </div>
            </a-form-model-item>

            <a-form-model-item
              :label="$t('components.billing_top_up.to_shop_balance')"
              class="expanded-option__form-item"
            >
              <div class="billing-section">
                <div class="billing-section__icon">
                  <a-avatar
                    shape="square"
                    :size="36"
                    icon="shop"
                    :src="smallShopLogoAbsolutePath"
                    :alt="shopName"
                  />
                </div>
                <div class="billing-section__description">
                  <span class="billing-section__strong">
                    {{ shopName }}
                  </span>
                  {{ getPriceWithCurrency(sellerBalance) }}
                </div>
              </div>
            </a-form-model-item>

            <a-form-model-item
              class="expanded-option__form-item expanded-option__input"
              prop="amount"
            >
              <template #label>
                {{ $t("components.billing_top_up.top_up_sum") }}

                (1
                <img
                  class="bonus-icon"
                  style="margin-left: 2px"
                  src="@/assets/icons/coin.svg"
                />
                = {{ getPriceWithCurrency(1, true) }})
              </template>

              <a-input-number
                ref="bonusesAmountInputRef"
                v-model="form.amount"
                :min="0"
                :precision="0"
                @keydown.enter="handleSubmit"
              />
              <img
                class="bonus-icon"
                src="@/assets/icons/coin.svg"
              />
            </a-form-model-item>
          </template>
        </div>
      </a-form-model>
    </a-spin>
  </a-modal>
</template>

<script lang="ts">
import { WrappedFormUtils } from "ant-design-vue/types/form/form"
import { reactive, ref, watch } from "vue"
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"

import i18n from "@/i18n"
import { CREATE_TICKET, UPDATE_REQUEST } from "@/modules/support/_store/actionTypes"
import dashboardService from "@/services/dashboardService"
import { HANDLE_SUPPORT_DRAWER } from "@/store/modules/notifications/mutationTypes"
import { GET_BALANCE } from "@/store/modules/user/actionTypes"
import { getFullImagePath } from "@/utils/cdn"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getPriceWithCurrency from "@/utils/getPriceWithCurrency"

type PaymentForm = {
  paymentMethod: "cashless" | "bonuses"
  amount: number
}

export default {
  name: "BillingTopUp",

  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },

  setup(props) {
    const formRef = ref<HTMLElement & WrappedFormUtils>()
    const bonusesAmountInputRef = ref<HTMLInputElement>()

    const validateAmount = (_: any, value: number | null, callback: Function) => {
      if (value < 1) {
        callback(new Error(i18n.t("validations.min_num", { count: 1 }) as string))
      } else if (value > sellerBonuses.value) {
        callback(new Error(i18n.t("components.billing_top_up.enough_bonuses_error") as string))
      } else {
        callback()
      }
    }

    const form = reactive<PaymentForm>({
      paymentMethod: "cashless",
      amount: 0
    })
    const formRules = {
      amount: [
        { required: true, message: i18n.t("validations.required_field") },
        { validator: validateAmount }
      ]
    }

    const sellerBonuses = ref<number>(0)
    const fetching = ref<boolean>(false)
    const updating = ref<boolean>(false)
    const mobilePhone = ref<string>("")

    const focusOnBonusesInput = () => {
      if (!bonusesAmountInputRef.value) return

      bonusesAmountInputRef.value.focus()
    }

    const fetchSellerBalance = async () => {
      fetching.value = true

      try {
        const response = await dashboardService.sellerBalance()
        const { data } = response

        sellerBonuses.value = data.bonuses || 0
        mobilePhone.value = data.mobile_phone
      } finally {
        fetching.value = false
      }
    }

    watch(
      () => props.visible,
      async (value: boolean) => {
        if (!value) return

        fetchSellerBalance()
        focusOnBonusesInput()

        if (!formRef.value) return

        formRef.value.resetFields()
      }
    )

    watch(
      () => form.paymentMethod,
      () => {
        form.amount = 0
      }
    )

    return {
      formRef,
      bonusesAmountInputRef,

      form,
      formRules,

      sellerBonuses,
      fetching,
      updating,
      mobilePhone
    }
  },

  computed: {
    ...mapState("user", {
      shopName: (state: any): string => state.profile.name,
      isOwner: (state: any): boolean => state.profile.is_owner,
      sellerBalance: (state: any): number => state.commissionBalance.balance,
      user: (state: any) => state.user
    }),

    ...mapGetters("user", ["smallShopLogoAbsolutePath"]),

    formDisabled() {
      return !this.isOwner && this.form.paymentMethod === "bonuses"
    },

    userImageSrc() {
      if (!this.user?.avatar?.img) {
        return ""
      }

      return getFullImagePath(this.user.avatar.img)
    }
  },

  methods: {
    getFormattedPrice,
    getPriceWithCurrency,

    ...mapActions("user", [GET_BALANCE]),
    ...mapActions("support", [CREATE_TICKET, UPDATE_REQUEST]),
    ...mapMutations("notifications", [HANDLE_SUPPORT_DRAWER]),

    async handleSubmit() {
      if (this.form.paymentMethod === "cashless") {
        this[HANDLE_SUPPORT_DRAWER]({
          subject: this.$t("tariffs.cpa_statistics.top_up_shop_balance.title"),
          text: this.$t("tariffs.cpa_statistics.top_up_shop_balance.body", {
            sum: this.getPriceWithCurrency(500, true)
          }),
          open: true
        })
        this.$emit("close")

        return
      }

      try {
        await this.formRef.validate()
      } catch {
        return
      }

      try {
        this.updating = true
        const response = await dashboardService.replenishByBonuses(this.form.amount)

        if (response.status === 200) {
          this.$message.success(this.$t("components.billing_top_up.bonuses_transferred_to_balance"))

          this[GET_BALANCE]()
          this.$emit("close")
        }
      } catch (e) {
        const { amount } = e?.response?.data
        const messages: string[] = amount || []

        messages.forEach((message: string) => this.$message.error(message))
      } finally {
        this.updating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.billing-top-up {
  &__choice-method {
    margin-bottom: 12px;
    color: #262626;
  }

  &__main-loader {
    display: flex;
    justify-content: center;
  }

  &__radio-group {
    display: block;

    & .ant-radio-wrapper-checked {
      color: #262626;
    }

    &__row {
      display: block;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.expanded-option {
  &__form-item {
    margin: 0;
    margin-bottom: 16px;
  }
}

.billing-section {
  background-color: #fafafa;
  padding: 12px 16px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;

  &__icon {
    width: 36px;
    min-height: 36px;
    margin-right: 12px;
    display: flex;
    justify-content: center;
    align-items: center;

    & img {
      max-height: 100%;
      max-width: 100%;
    }
  }

  &__description {
    flex: 1;
    line-height: 21px;
  }

  &__strong {
    font-weight: 500;
    color: #262626;
  }
}
.bonus-icon {
  width: 12px;
  height: 12px;
  margin: 0 0 -1px 4px;

  object-fit: contain;
  display: inline-block;
}
</style>
